import { Building2, KeyRound, Users } from "lucide-react";
import { NoteBanner } from "@/components/ui/NoteBanner";
import { formatAmount } from "@/lib/utils/formatters";
import {
  formatDateMMDDYY,
  formatPhoneNumber,
} from "../../../utils/applicationFormatters";
import { resolvePropertyOtherIndividualsFlag } from "../../../utils/propertyTabValidator";
import { PROPERTY_NOT_WORKING_ALERT_MESSAGE } from "../propertyTabHelpers";
import styles from "../../../styles/applicationDetail.module.css";
import type {
  CoResidentFormRow,
  CoResidentViewSectionProps,
  LeasingAgentViewSectionProps,
  PropertyViewSectionProps,
} from "../../../types/tabs";

export function PropertyViewSection({
  applicationAllData,
  property,
  displayInviteCode,
  showNotWorkingAlert,
  onOpenProperty,
}: PropertyViewSectionProps) {
  return (
    <div className={styles.section} data-testid="application-property-tab-section-moving-to-view">
      <div className={styles.propertySectionHeadingRow}>
        <h2 className={styles.sectionTitle}>
          <span className={styles.sectionTitleIcon} aria-hidden="true">
            <Building2 className="h-4 w-4" />
          </span>
          Where are you moving to?
        </h2>
        {displayInviteCode ? (
          <div className={styles.propertyInviteBadge}>
            Invitation: {displayInviteCode}
          </div>
        ) : null}
      </div>
      {showNotWorkingAlert && (
        <NoteBanner
          className="mb-4"
          data-testid="application-property-tab-not-working-alert-view"
        >
          {PROPERTY_NOT_WORKING_ALERT_MESSAGE}
        </NoteBanner>
      )}
      {property && (
        <div className={styles.infoRow}>
          <div className={styles.infoColumn}>
            <h6>Name</h6>
            {applicationAllData?.property?.property_id ? (
              <div className={styles.propertyLinkField}>
                <button
                  type="button"
                  className={styles.propertyLink}
                  onClick={() => {
                    const propertyId = applicationAllData.property?.property_id;
                    if (propertyId != null) {
                      void onOpenProperty(propertyId);
                    }
                  }}
                >
                  {property.property_name}
                </button>
              </div>
            ) : (
              <p>{property.property_name}</p>
            )}
            <p>Managed by: {property.company_name}</p>
          </div>
          <div className={styles.infoColumn}>
            <h6>Address</h6>
            <div>
              <p className="m-0">{property.property_address}</p>
              {property.property_address2 && (
                <p className="m-0">{property.property_address2}</p>
              )}
              <p className="m-0">
                {property.property_city} {property.property_state}{" "}
                {property.property_zipcode}
              </p>
            </div>
          </div>
          <div className={styles.infoColumn}>
            <h6>Monthly Rent Amount</h6>
            <p>
              {applicationAllData?.rent_amount
                ? formatAmount(applicationAllData.rent_amount)
                : ""}
            </p>
          </div>
        </div>
      )}

      <div className={styles.infoRow}>
        <div className={styles.infoColumn}>
          <h6>Unit Number</h6>
          <p>{applicationAllData?.unit_number}</p>
        </div>
        <div className={styles.infoColumn}>
          <h6>Lease Start Date</h6>
          <p>{formatDateMMDDYY(applicationAllData?.lease_start_date)}</p>
        </div>
        <div className={styles.infoColumn}>
          <h6>Lease Duration</h6>
          <p>
            {applicationAllData?.lease_duration || 12}{" "}
            {String(applicationAllData?.lease_duration) === "1" ? "month" : "months"}
          </p>
        </div>
      </div>
    </div>
  );
}

export function LeasingAgentViewSection({
  hasViewLeasingInfo,
  leasingConsultantLabel,
  viewLeasing,
}: LeasingAgentViewSectionProps) {
  return (
    <div className={styles.section} data-testid="application-property-tab-section-leasing-agent-view">
      <h2 className={styles.sectionTitle}>
        <span className={styles.sectionTitleIcon} aria-hidden="true">
          <KeyRound className="h-4 w-4" />
        </span>
        Leasing Agent
      </h2>
      {hasViewLeasingInfo && (
        <div className={styles.infoRow}>
          <div className={styles.infoColumn}>
            <h6>{leasingConsultantLabel}/Property Manager</h6>
            <p>{viewLeasing.name || "—"}</p>
          </div>
          <div className={styles.infoColumn}>
            <h6>{leasingConsultantLabel}/Property Manager Phone</h6>
            <p>
              {viewLeasing.phone
                ? formatPhoneNumber(viewLeasing.phone)
                : "—"}
            </p>
          </div>
          <div className={styles.infoColumn}>
            <h6>{leasingConsultantLabel}/Property Manager Email</h6>
            <p>{viewLeasing.email || "—"}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function CoResidentViewSection({
  otherIndividuals,
}: CoResidentViewSectionProps) {
  const hasOtherIndividuals =
    otherIndividuals === true ||
    otherIndividuals === "1" ||
    otherIndividuals === 1;

  return (
    <div className={styles.section} data-testid="application-property-tab-section-co-resident">
      <h2 className={styles.sectionTitle}>
        <span className={styles.sectionTitleIcon} aria-hidden="true">
          <Users className="h-4 w-4" />
        </span>
        Co-Resident
      </h2>
      <div className={styles.infoRow}>
        <div className={styles.infoColumn}>
          <h6>
            Will other individuals be on this lease? (Do not include children
            under the age of 18)
          </h6>
          <p>{hasOtherIndividuals ? "Yes" : "No"}</p>
        </div>
      </div>
    </div>
  );
}

export function resolveCoResidentViewOtherIndividuals(
  applicationAllData: Parameters<typeof resolvePropertyOtherIndividualsFlag>[2],
  applicationData: Parameters<typeof resolvePropertyOtherIndividualsFlag>[1],
  coResidents: CoResidentFormRow[],
): boolean {
  return (
    resolvePropertyOtherIndividualsFlag(undefined, applicationData, applicationAllData) ||
    coResidents.length > 0
  );
}
